// SPDX-License-Identifier: SEE LICENSE
//
// CertificateCard — a prominent inline mint-and-show panel on the
// session view. Doesn't replace the dedicated /sessions/:id/certificate
// page; it makes the cert visible at-a-glance during the demo, with a
// one-click mint button and live cryptographic anchors.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getSessionCertificate, mintSessionCertificate } from '../api';

interface CertSummary {
  rootHex?: string;
  rekorId?: string;
  ots?: { calendars?: number; calendarConfirmed?: boolean };
  issuer?: { alg?: string; spkiSha256?: string };
  pdfUrl?: string;
  bundleUrl?: string;
  short?: string;
  passed?: number;
  total?: number;
  mintedAt?: number;
}

function pickCert(payload: unknown): CertSummary | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, any>;
  const cert = p.certificate ?? p; // accept either { certificate: {...} } or {...}
  if (!cert || typeof cert !== 'object') return null;
  // The bundle (VC JSON-LD) holds the real Sigstore Rekor + OTS anchors at
  // `bundle.credentialSubject.anchors.*`. The legacy POST response shape
  // surfaces `rekorLogIndex` at top-level. Cover both.
  const bundleSubject = cert.bundle?.credentialSubject as Record<string, any> | undefined;
  const bundleAnchors = bundleSubject?.anchors as Record<string, any> | undefined;
  const rekorIdResolved =
    cert.rekorId ??
    cert.rekor_id ??
    cert.rekorLogIndex ??
    cert.rekor_log_index ??
    cert.rekor?.id ??
    bundleAnchors?.rekorLogIndex;
  const otsResolved = cert.ots ?? cert.timestamp ?? (bundleAnchors?.openTimestampsCalendars
    ? {
        calendars: Array.isArray(bundleAnchors.openTimestampsCalendars)
          ? bundleAnchors.openTimestampsCalendars.length
          : undefined,
        calendarConfirmed: bundleAnchors.openTimestampsStatus === 'calendar-confirmed',
      }
    : undefined);
  const issuerResolved = cert.issuer ?? (bundleSubject?.deviceAttestation
    ? {
        alg: 'EC P-384',
        spkiSha256: bundleSubject.deviceAttestation.keyFingerprintSha256,
      }
    : undefined);
  const out: CertSummary = {
    mintedAt: cert.mintedAt ?? cert.minted_at ?? cert.created_at ?? Date.now(),
  };
  if (cert.merkleRootHex ?? cert.rootHex ?? cert.merkle_root_hex) {
    out.rootHex = cert.merkleRootHex ?? cert.rootHex ?? cert.merkle_root_hex;
  }
  if (rekorIdResolved != null) out.rekorId = String(rekorIdResolved);
  if (otsResolved) out.ots = otsResolved;
  if (issuerResolved) out.issuer = issuerResolved;
  if (cert.pdfUrl ?? cert.pdf_url) out.pdfUrl = cert.pdfUrl ?? cert.pdf_url;
  if (cert.bundleUrl ?? cert.bundle_url) out.bundleUrl = cert.bundleUrl ?? cert.bundle_url;
  if (cert.short) out.short = cert.short;
  if (cert.passed != null) out.passed = cert.passed;
  if (cert.total != null) out.total = cert.total;
  return out;
}

export function CertificateCard({
  sessionId,
  liveCertificate,
  autoMintTrigger,
}: {
  sessionId: string;
  liveCertificate: Record<string, unknown> | null;
  /** Set true when the session reaches a "fully done" state to trigger an auto-mint. */
  autoMintTrigger: boolean;
}) {
  const [cert, setCert] = useState<CertSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTriedFetch, setHasTriedFetch] = useState(false);

  // Initial fetch — pick up any cert minted on a prior visit. Backend
  // returns 404 unless the session is eligible, so this never triggers
  // an empty-tree mint.
  useEffect(() => {
    let cancelled = false;
    getSessionCertificate(sessionId)
      .then((p) => {
        if (cancelled) return;
        setCert(pickCert(p));
      })
      .catch(() => {
        /* not found = no cert yet */
      })
      .finally(() => setHasTriedFetch(true));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Live SSE update — when backend publishes certificate.minted.
  useEffect(() => {
    if (liveCertificate) setCert(pickCert(liveCertificate));
  }, [liveCertificate]);

  // Auto-mint when the run reaches a "done enough" state. Re-mint even
  // if a (stale / pre-completion) cert is showing — the new mint will
  // overwrite the stored one with the current real session aggregate.
  const [autoMinted, setAutoMinted] = useState(false);
  useEffect(() => {
    if (!autoMintTrigger) return;
    if (autoMinted) return;
    if (!hasTriedFetch) return;
    setAutoMinted(true);
    void doMint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMintTrigger, hasTriedFetch, autoMinted]);

  const doMint = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await mintSessionCertificate(sessionId);
      setCert(pickCert(payload));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const status = cert
    ? 'Minted'
    : busy
      ? 'Minting…'
      : 'Awaiting';
  const minted = !!cert;
  return (
    <div
      style={{
        border: '1px solid rgba(120,160,160,0.16)',
        borderRadius: 14,
        background: 'rgba(9,16,26,0.55)',
        padding: '16px 22px',
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 280, flexShrink: 0 }}>
        <span
          style={{
            width: 42,
            height: 46,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: minted ? '#15ddc9' : '#6f878e',
            flexShrink: 0,
            filter: minted ? 'drop-shadow(0 0 8px rgba(20,222,205,0.5))' : 'none',
          }}
        >
          <svg width="38" height="42" viewBox="0 0 24 26" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M12 1 2 5v7c0 6 5 10 10 12 5-2 10-6 10-12V5z" fill="rgba(20,222,205,0.07)" />
            <rect x="8.5" y="11" width="7" height="6" rx="1.2" />
            <path d="M9.5 11V9.2a2.5 2.5 0 0 1 5 0V11" />
          </svg>
        </span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1.5, color: '#e8f2f1' }}>
            DIGITAL DEVICE CERTIFICATE
          </div>
          <div style={{ fontSize: 12, marginTop: 5 }}>
            <span style={{ color: minted ? '#15ddc9' : '#f5bf45', fontWeight: 600 }}>{status}</span>
            <span style={{ color: '#5d7178' }}>
              {minted ? ' · cryptographically sealed' : ' · run completes will trigger this'}
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr 1.3fr 1fr 0.8fr auto',
          alignItems: 'center',
          gap: 0,
        }}
      >
        <CertCell
          label="MERKLE ROOT"
          value={cert?.rootHex ? `0x${cert.rootHex.slice(0, 12)}…` : '—'}
          sub={cert?.rootHex ? 'RFC-6962' : 'awaiting'}
        />
        <CertCell
          label="SIGSTORE REKOR"
          value={cert?.rekorId ? `#${cert.rekorId}` : 'pending'}
          sub={cert?.rekorId ? 'transparency log' : ''}
        />
        <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.55 }}>
          <svg width="200" height="44" viewBox="0 0 200 44" fill="none">
            <g stroke="rgba(20,222,205,0.35)" strokeWidth="0.6">
              <line x1="20" y1="14" x2="60" y2="8" />
              <line x1="60" y1="8" x2="92" y2="22" />
              <line x1="92" y1="22" x2="130" y2="12" />
              <line x1="130" y1="12" x2="168" y2="26" />
              <line x1="40" y1="34" x2="92" y2="22" />
              <line x1="92" y1="22" x2="120" y2="36" />
              <line x1="168" y1="26" x2="184" y2="16" />
            </g>
            <g fill="#15ddc9">
              <circle cx="20" cy="14" r="1.6" />
              <circle cx="60" cy="8" r="1.6" />
              <circle cx="92" cy="22" r="2.2" />
              <circle cx="130" cy="12" r="1.6" />
              <circle cx="168" cy="26" r="1.6" />
              <circle cx="40" cy="34" r="1.6" />
              <circle cx="120" cy="36" r="1.6" />
              <circle cx="184" cy="16" r="1.6" />
            </g>
          </svg>
        </div>
        <CertCell
          label="OPENTIMESTAMPS"
          value={cert?.ots?.calendarConfirmed ? 'BTC confirmed' : cert?.ots ? 'pending' : 'BTC anchor'}
          sub={cert?.ots?.calendars ? `${cert.ots.calendars} calendars` : ''}
        />
        <CertCell label="ISSUER" value={cert?.issuer?.alg ?? 'P-384'} sub={cert?.issuer?.spkiSha256 ? `0x${cert.issuer.spkiSha256.slice(0, 8)}…` : ''} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 18 }}>
          {!cert ? (
            <button
              onClick={doMint}
              disabled={busy}
              style={{
                fontSize: 11.5,
                color: '#f5bf45',
                background: 'rgba(245,191,69,0.06)',
                border: '1px solid rgba(245,191,69,0.4)',
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {busy ? 'Minting…' : 'Mint now'}
            </button>
          ) : (
            <Link
              to={`/sessions/${sessionId}/certificate`}
              style={{
                fontSize: 11.5,
                color: '#15ddc9',
                background: 'rgba(20,222,205,0.06)',
                border: '1px solid rgba(20,222,205,0.4)',
                borderRadius: 8,
                padding: '6px 12px',
                textDecoration: 'none',
              }}
            >
              Open →
            </Link>
          )}
        </div>
      </div>
      {error && (
        <div style={{ position: 'absolute', bottom: 4, right: 22, fontSize: 11, color: '#f06a5e' }}>{error}</div>
      )}
    </div>
  );
}

function CertCell({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div style={{ padding: '0 18px', borderLeft: '1px solid rgba(120,160,160,0.12)' }}>
      <div style={{ fontSize: 10, letterSpacing: 1.2, color: '#6f878e', fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 14,
          color: '#e8f2f1',
          marginTop: 6,
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: '#5d7178', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}
